/**
 * Some shared helpers for IssuesV2.
 */
import { formatISO } from 'date-fns';
import type { SetRequired } from 'type-fest';

import { Category, CATEGORY_TO_ISSUE_TYPES, IgnoredKind, IssueScopeKind, RISK_ISSUE_TYPES } from './common';
import type {
  GlobalScope,
  IssueType,
  LicensingIssueType,
  ProjectScope,
  QualityIssueType,
  ReleaseGroupScope,
  RiskIssueType,
  Scope,
  VulnIssueType,
} from './common';
import type { FilterByCategory } from './filters';
import type { Project } from './response';

const GLOBAL_SCOPE: GlobalScope = {
  type: IssueScopeKind.Global,
};

export const getGlobalScope = (): GlobalScope => GLOBAL_SCOPE;

export const getProjectScope = (id: string, revision?: string, revisionScanId?: number): ProjectScope => ({
  type: IssueScopeKind.Project,
  id,
  revision,
  revisionScanId,
});

//  Technically these can be strings or numbers, since they are cast to numbers on the BE anyway.
export const getReleaseGroupScope = (id: string, release: number, releaseScanId?: number): ReleaseGroupScope => ({
  type: IssueScopeKind.ReleaseGroup,
  id,
  release,
  releaseScanId,
});

/**
 * Test if a scope is global.
 *
 * @param {Scope} scope - A given scope
 */
export const isGlobalScope = (scope: Scope): scope is GlobalScope => scope.type === 'global';

/**
 * Test if a scope is project.
 *
 * @param {Scope} scope - A given scope.
 */
export const isProjectScope = (scope: Scope): scope is ProjectScope => scope.type === 'project';

/**
 * Test if a scope is a project scope with a specific revision.
 *
 * @param {Scope} scope - A given scope.
 */
export const isRevisionScope = (scope: Scope): scope is SetRequired<ProjectScope, 'revision'> =>
  isProjectScope(scope) && !!scope.revision;

export const isRevisionComparisonScope = (scope: Scope): scope is SetRequired<ProjectScope, 'revision' | 'compareTo'> =>
  isProjectScope(scope) && !!scope.revision && !!scope.compareTo;

/**
 * Test if a scope is releaseGroup.
 *
 * @param {Scope} scope - A given scope.
 */
export const isReleaseGroupScope = (scope: Scope): scope is ReleaseGroupScope => scope.type === 'releaseGroup';

/**
 * Test if a scope is valid to be used for Release Group Release issue comparison
 *
 * @param {Scope} scope - A given scope.
 */
export const isReleaseComparisonScope = (scope: Scope): scope is SetRequired<ReleaseGroupScope, 'compareTo'> =>
  isReleaseGroupScope(scope) && !!scope.release && !!scope.compareTo;

/**
 * API Endpoints used for issues v2 queries.
 */
export const ENDPOINTS = {
  CATEGORY_SUMMARY: '/api/v2/issues/categories',
  TYPES_SUMMARY: '/api/v2/issues/types',
  FETCH_ISSUES: '/api/v2/issues',
  FETCH_ISSUE_LICENSES: '/api/revisions',
  FETCH_REVISIONS: '/api/v2/issues/revisions',
  STATUS_SUMMARY: '/api/v2/issues/statuses',
  LICENSE_LIST: '/api/v2/issues/license-list',
  UPDATE_ISSUES: '/api/v2/issues',
  GENERATE_ISSUES: '/api/v2/issues',
  EXCEPTIONS_LIST: '/api/v2/issues/exceptions',
  PACKAGE_MANAGERS: '/api/v2/issues/package-managers',
  CWES: '/api/v2/issues/cwes',
};

/** Type predicate for testing if an issueType is of LicensingIssueType */
export const isLicensingIssueType = (issueType: IssueType): issueType is LicensingIssueType =>
  CATEGORY_TO_ISSUE_TYPES.licensing.includes(issueType);

/**
 * Type predicate for testing if an issueType is of RiskIssueType
 * Note that risk issues are a subset of quality issues
 */
export const isRiskIssueType = (issueType: IssueType): issueType is RiskIssueType =>
  RISK_ISSUE_TYPES.includes(issueType as RiskIssueType);

/** Type predicate for testing if an issueType is of VulnIssueType */
export const isVulnIssueType = (issueType: IssueType): issueType is VulnIssueType =>
  CATEGORY_TO_ISSUE_TYPES.vulnerability.includes(issueType);

/** Type predicate for testing if an issueType is of QualityIssueType */
export const isQualityIssueType = (issueType: IssueType): issueType is QualityIssueType =>
  CATEGORY_TO_ISSUE_TYPES.quality.includes(issueType);

//  Determines if any of a given `issue.projects` have been ignored by an ignore rule
export const isAnyIssueProjectIgnoredByRule = (projects: Project[]) => projects.some(
  (issueProject) => issueProject.ignoredKind?.ignoreType === IgnoredKind.IssueException,
);

//  We generally display the "vulnerability" category as "security" to the user.
export const getCategoryDisplayName = (category: Category): Category.Licensing | Category.Quality | 'security' =>
  category === Category.Vulnerability ? 'security' : category;

// Various utilities for working with the `foundAfter` filter.

/** All the option types supported for the `firstFound` filter. */
export enum FoundAfterOption {
  Anytime = 'anytime',
  Last7Days = 'last7days',
  Last14Days = 'last14days',
  Last30Days = 'last30days',
}

/**
 * Converts the type of a filter object with query-style foundAfter values to
 * values for the store.  This lets us derive the filters on the store from the
 * filters in the shared query, only changing the fields that change.
 *
 * Specifically, this changes { foundAfter?: string } to { foundAfter?: FoundAfterOption }
 *
 * TODO: This should be combined with other conversions if there are more fields
 * that need to change.
 */
export type ConvertFoundAfterFilterForUser<C extends Category, T> =
  T extends FilterByCategory<C>
    ? T & { foundAfter?: FoundAfterOption } // The query has type `string` so this narrows it.
    : T;

export const isValidFoundAfterOption = (value: unknown): value is FoundAfterOption =>
  Object.values(FoundAfterOption).includes(value as FoundAfterOption);

/**
 * Converts a FoundAfterOption to a date.  The dates are calculated by creating
 * a date that is midnight of N days ago in your local timezone.
 */
export const dateForFoundAfterOption = (option: FoundAfterOption | undefined): string | undefined => {
  switch (option) {
    case 'last7days':
      return formatISO(new Date().setDate(new Date().getDate() - 7));
    case 'last14days':
      return formatISO(new Date().setDate(new Date().getDate() - 14));
    case 'last30days':
      return formatISO(new Date().setDate(new Date().getDate() - 30));
    default:
      return undefined;
  }
};

/**
 * Replaces the foundAfter option with the date for the option. This is useful for
 * validating the filters before they are saved, as the filter is stored as a relative
 * date string, but needs to be validated as a date. Anytime removes the date constraint.
 *
 * @param filter - The filter to replace the foundAfter option with the date for the option.
 * @param option - The option to replace the foundAfter option with the date for.
 * @returns The filter with the foundAfter option replaced with the date for the option.
 */
export const replaceFilterFoundAfterWithDate = (
  filter: FilterByCategory<Category>,
  option: FoundAfterOption | undefined,
) => {
  if (!option) {
    return filter;
  }
  if (option === FoundAfterOption.Anytime) {
    const filterWithoutFoundAfter = { ...filter };
    delete filterWithoutFoundAfter.foundAfter;
    return filterWithoutFoundAfter;
  }
  const date = dateForFoundAfterOption(option);
  if (date) {
    return {
      ...filter,
      foundAfter: date,
    };
  }
  return filter;
};
